import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Camera,
  Video,
  CalendarCheck,
  Heart,
  Building2,
  Music,
  Wine,
  MapPin,
} from "lucide-react";
import { PHOTOS } from "../mock";

const TILES = [
  {
    key: "evenements",
    label: "Événements musicaux",
    subtitle: "4 formules · À partir de 200 € · Sur devis",
    to: "/prestations/evenements",
    image: "https://customer-assets-4nw71qhi.emergentagent.net/job_musicien-variete/artifacts/2vw6rhl6_EDBEAAC3-9BBB-4331-8474-88D073E47173.webp",
    Icon: CalendarCheck,
    accent: "text-red-500",
    border: "hover:border-red-500/50",
    featured: true,
  },
  {
    key: "photos",
    label: "Photos",
    subtitle: "Captations live · backstage · scène",
    to: "/prestations/photos",
    image: PHOTOS.stageRedBlue,
    Icon: Camera,
    accent: "text-red-500",
    border: "hover:border-red-500/50",
  },
  {
    key: "videos",
    label: "Vidéos",
    subtitle: "Extraits de concerts · sessions",
    to: "/prestations/videos",
    image: PHOTOS.heroRedBlue,
    Icon: Video,
    accent: "text-blue-600",
    border: "hover:border-blue-600/50",
  },
];

const EVENT_TYPES = [
  {
    Icon: Heart,
    label: "Mariages",
    detail: "Anniversaires · Fêtes privées",
    accent: "text-red-500",
  },
  {
    Icon: Building2,
    label: "Soirées d'entreprise",
    detail: "Cocktails · Inaugurations",
    accent: "text-blue-600",
  },
  {
    Icon: Music,
    label: "Festivals · Concerts",
    detail: "Salles · Plateaux artistiques",
    accent: "text-red-500",
  },
  {
    Icon: Wine,
    label: "Bars · Restaurants",
    detail: "Pianos-bars · Cabarets",
    accent: "text-blue-600",
  },
  {
    Icon: MapPin,
    label: "Événements municipaux",
    detail: "Fêtes de village · Comités",
    accent: "text-red-500",
  },
];

export default function Prestations() {
  return (
    <div
      className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16"
      data-testid="prestations-page"
    >
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">
          Accueil
        </Link>
        <span>/</span>
        <span className="text-white">Événementiel</span>
      </div>

      <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            05 · Sur scène
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            Événementiel
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
              Booking · Photos · Vidéos
            </span>
          </div>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Découvrez les formules musicales proposées par <span className="text-red-500 font-medium">Vince Rodi</span>, ainsi qu'un aperçu visuel des concerts : photos de scène, captations live et sessions acoustiques.
          </p>
        </div>
      </div>

      {/* TYPES D'ÉVÉNEMENTS */}
      <div className="mb-14" data-testid="event-types-section">
        <div className="flex items-end justify-between mb-6 border-b border-white/10 pb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-2">
              Où je peux jouer
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
              Styles d'événements
            </h2>
          </div>
          <Link
            to="/prestations/evenements"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-400 hover:text-white"
            data-testid="event-types-cta"
          >
            Voir les formules <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {EVENT_TYPES.map((e, i) => (
            <div
              key={e.label}
              data-testid={`event-type-${i}`}
              className="border border-white/10 hover:border-white/30 transition-colors p-5 bg-neutral-950/60"
            >
              <e.Icon size={22} strokeWidth={1.4} className={`${e.accent} mb-4`} />
              <div className="font-display text-lg leading-tight tracking-tight mb-1">
                {e.label}
              </div>
              <div className="text-[11px] text-neutral-500 leading-snug">{e.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* TILES */}
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="prestations-tiles">
        {TILES.map((t) => (
          <Link
            key={t.key}
            to={t.to}
            data-testid={`prestations-tile-${t.key}`}
            className={`group relative block border border-white/10 ${t.border} bg-neutral-950 overflow-hidden transition-all ${
              t.featured ? "md:col-span-2" : ""
            }`}
          >
            <div className={`${t.featured ? "aspect-[16/6]" : "aspect-[4/3]"} overflow-hidden bg-black relative`}>
              <img
                src={t.image}
                alt={t.label}
                className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className={`absolute top-5 left-5 ${t.accent}`}>
                <t.Icon size={28} strokeWidth={1.5} />
              </div>
              {t.featured && (
                <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.3em] bg-red-500 text-white px-2.5 py-1">
                  Booking
                </div>
              )}
            </div>
            <div className="p-6 md:p-8 flex items-end justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                  {t.subtitle}
                </div>
                <h2 className="font-display uppercase text-5xl md:text-6xl leading-none tracking-tight">
                  {t.label}
                </h2>
              </div>
              <ArrowUpRight
                size={32}
                strokeWidth={1.25}
                className="text-neutral-400 group-hover:text-white group-hover:rotate-12 transition-all"
              />
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-14 border border-white/15 p-6 md:p-8 text-sm" data-testid="prestations-note">
        <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">À venir</div>
        <div className="text-neutral-300">
          Les photos et vidéos officielles seront mises en ligne prochainement. Cette page accueillera les meilleures captations des concerts de la tournée 2026.
        </div>
      </div>
    </div>
  );
}
